import { type MouseEvent, useCallback, useState } from 'react';
import { type Reference } from '@apollo/client';

import { Analytics, formatContainers } from '@trello/atlassian-analytics';
import { sendErrorEvent } from '@trello/error-reporting';
import { intl } from '@trello/i18n';
import type { Board, Collaborator, Organization } from '@trello/model-types';
import { Button } from '@trello/nachos/button';
import { showFlag } from '@trello/nachos/experimental-flags';

import { useRemoveWorkspaceCollaboratorFromBoardMutation } from './RemoveWorkspaceCollaboratorFromBoardMutation.generated';
import { useWorkspaceCollaboratorItemFragment } from './WorkspaceCollaboratorItemFragment.generated';
import { useWorkspaceMemberItemFragment } from './WorkspaceMemberItemFragment.generated';

import styles from './RemoveWorkspaceCollaboratorFromBoardButton.less';

interface RemoveWorkspaceCollaboratorFromBoardButtonProps {
  boardName?: string;
  idMember: string;
  idOrganization: string;
  idBoard: string;
}

export const RemoveWorkspaceCollaboratorFromBoardButton = ({
  boardName,
  idMember,
  idOrganization,
  idBoard,
}: RemoveWorkspaceCollaboratorFromBoardButtonProps) => {
  const [isRemoving, setIsRemoving] = useState(false);

  const { data: collaborator } = useWorkspaceCollaboratorItemFragment({
    from: { id: idMember },
    optimistic: true,
  });

  const { data: member } = useWorkspaceMemberItemFragment({
    from: { id: idMember },
    optimistic: true,
  });

  const fullName = collaborator?.fullName || member?.fullName;

  const [removeMemberFromBoard] =
    useRemoveWorkspaceCollaboratorFromBoardMutation();

  const onRemove = useCallback(
    async (e: MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      setIsRemoving(true);

      const traceId = Analytics.startTask({
        taskName: 'edit-board/members/remove',
        source: 'workspaceMembersScreen',
      });

      try {
        await removeMemberFromBoard({
          variables: { idBoard, idMember, traceId },

          // To re-render the Workspace guest page correctly, we needed to manually update the Apollo cache
          // because the removeMemberFromBoard mutation only updates the board in the cache,
          // not the Organization Collaborator list or the Collaborator's boardCount
          update(cache, result) {
            if (!result.data?.removeMemberFromBoard) {
              return;
            }
            cache.modify<Board>({
              id: cache.identify({
                id: idBoard,
                __typename: 'Board',
              }),
              fields: {
                memberships(currentMemberships = [], { readField }) {
                  return currentMemberships.filter((membershipRef) => {
                    return readField('idMember', membershipRef) !== idMember;
                  });
                },
              },
            });

            if (member) {
              cache.modify<Organization>({
                id: cache.identify({
                  id: idOrganization,
                  __typename: 'Organization',
                }),
                fields: {
                  boardCounts(currentBoardCounts, { readField }) {
                    return currentBoardCounts.map((boardCountObj) => {
                      if (readField('idMember', boardCountObj) === idMember) {
                        const newBoardCount =
                          (readField('boardCount', boardCountObj) as number) -
                          1;
                        return {
                          ...boardCountObj,
                          boardCount: newBoardCount,
                        };
                      } else {
                        return boardCountObj;
                      }
                    });
                  },
                },
              });
            } else if (collaborator) {
              cache.modify<Collaborator>({
                id: cache.identify({
                  id: idMember,
                  __typename: 'Collaborator',
                }),
                fields: {
                  boardCount(currentBoardCount = 0) {
                    return currentBoardCount - 1;
                  },
                },
              });
            }

            if (collaborator?.boardCount === 1) {
              cache.modify<Organization>({
                id: cache.identify({
                  id: idOrganization,
                  __typename: 'Organization',
                }),
                fields: {
                  collaborators(existingCollaborators = [], { readField }) {
                    return existingCollaborators.filter((collaboratorRef) => {
                      return (
                        readField('id', collaboratorRef as Reference) !==
                        idMember
                      );
                    });
                  },
                },
              });
            }

            showFlag({
              id: 'remove-guest-from-team-boards',
              title: intl.formatMessage(
                {
                  id: 'templates.org_member_activity.remove-member-from-board-success',
                  defaultMessage: '{name} was removed from {boardName}.',
                  description: '{name} was removed from {boardName}.',
                },
                {
                  name: fullName,
                  boardName,
                },
              ),
              appearance: 'success',
              isAutoDismiss: true,
              msTimeout: 5000,
            });

            Analytics.taskSucceeded({
              taskName: 'edit-board/members/remove',
              source: 'workspaceMembersScreen',
              traceId,
            });

            Analytics.sendTrackEvent({
              action: 'removed',
              actionSubject: 'member',
              source: 'workspaceMembersScreen',
              containers: formatContainers({
                idBoard,
                idOrganization,
                workspaceId: idOrganization,
              }),
              attributes: {
                removedFrom: 'board',
                taskId: traceId,
              },
            });
          },
        });
      } catch (error) {
        showFlag({
          id: 'remove-guest-from-team-boards',
          title: intl.formatMessage(
            {
              id: 'templates.org_member_activity.remove-member-from-workspace-error',
              defaultMessage: 'Couldn’t remove {name}. Try again.',
              description: 'Couldn’t remove {name}. Try again.',
            },
            {
              name: fullName,
            },
          ),
          appearance: 'error',
          isAutoDismiss: true,
          msTimeout: 5000,
        });

        Analytics.taskFailed({
          taskName: 'edit-board/members/remove',
          source: 'workspaceMembersScreen',
          traceId,
          error,
        });

        sendErrorEvent(error, {
          tags: {
            ownershipArea: 'trello-web-eng',
          },
          extraData: {
            component: 'WorkspaceCollaboratorBoardListItem',
          },
        });
      }
    },
    [
      boardName,
      collaborator,
      fullName,
      idBoard,
      idMember,
      idOrganization,
      member,
      removeMemberFromBoard,
    ],
  );
  return (
    <Button
      appearance="danger"
      className={styles.removeButton}
      isDisabled={isRemoving}
      onClick={onRemove}
    >
      {intl.formatMessage({
        id: 'templates.org_member_activity.remove',
        defaultMessage: 'Remove',
        description: 'Button that removes the user from the board',
      })}
    </Button>
  );
};
